import cloneDeep from 'lodash/cloneDeep';
import type { ConfigType } from '@plone/registry';
import type { BlockConfigBase } from '@plone/types';
import CardBlockInfo from '@ifpb/portal/components/Blocks/CardBlock';
import MainImageBlockInfo from '@ifpb/portal/components/Blocks/MainImageBlock';
import {
  servicosSchemaEnhancer,
  carroselSchemaEnhancer,
} from '@ifpb/portal/components/Blocks/Listing/schema';
import ServicosListingBlockTemplate from '@ifpb/portal/components/Blocks/Listing/ServicosTemplate';
import CarrouselTemplate from '@ifpb/portal/components/Blocks/Listing/CarrouselTemplate';
import {
  showcaseSchemaEnhancer,
  fullTeaserSchemaEnhancer,
} from '@ifpb/portal/components/Blocks/Teaser/schema';
import ShowcaseTeaserBlockTemplate from '@ifpb/portal/components/Blocks/Teaser/ShowcaseTemplate';
import FullTeaserBlockTemplate from '@ifpb/portal/components/Blocks/Teaser/FullTeaserTemplate';
import { sliderBlockSchemaEnhancer } from '@ifpb/portal/components/Blocks/Slider/schema';

import { defineMessages } from 'react-intl';

defineMessages({
  cardBlock: {
    id: 'Card Block',
    defaultMessage: 'Ícone e Texto',
  },
  mainImageBlock: {
    id: 'Main Image Block',
    defaultMessage: 'Bloco de Imagem Principal',
  },
});

declare module '@plone/types' {
  export interface BlocksConfigData {
    cardBlock: BlockConfigBase;
    mainImageBlock: BlockConfigBase;
  }
}

function installBlocks(config: ConfigType) {
  config.blocks.blocksConfig.cardBlock = CardBlockInfo;
  config.blocks.blocksConfig.mainImageBlock = MainImageBlockInfo;

  return config;
}

function defaultBlocks(config: ConfigType) {
  config.blocks.initialBlocks = {
    ...config.blocks.initialBlocks,
    'News Item': [
      { '@type': 'title', fixed: true, required: true },
      { '@type': 'description', fixed: true, required: true },
      { '@type': 'mainImageBlock', align: 'left', theme: 'default', size: 's' },
      { '@type': 'slate' },
    ],
  };

  return config;
}

function installVariations(config: ConfigType) {
  // Listing Block Variations
  const listingVariations = cloneDeep(
    config.blocks.blocksConfig.listing.variations,
  );
  if (
    listingVariations.filter((item: any) => item.id === 'servicos').length === 0
  ) {
    (config.blocks.blocksConfig as any).listing.variations = [
      ...listingVariations,
      {
        id: 'servicos',
        title: 'Serviços',
        template: ServicosListingBlockTemplate,
        schemaEnhancer: servicosSchemaEnhancer,
      },
    ];
  }

  // Add Carousel variation to listing
  if (
    listingVariations.filter((item: any) => item.id === 'carousel').length === 0
  ) {
    (config.blocks.blocksConfig as any).listing.variations = [
      ...(config.blocks.blocksConfig as any).listing.variations,
      {
        id: 'carousel',
        title: 'Carrossel',
        template: CarrouselTemplate,
        schemaEnhancer: carroselSchemaEnhancer,
      },
    ];
  }

  // Teaser Block Variations
  const teaserVariations =
    cloneDeep((config.blocks.blocksConfig as any).teaser.variations) || [];

  const newVariations = [
    {
      id: 'showcase',
      title: 'Destaque Principal',
      template: ShowcaseTeaserBlockTemplate,
      schemaEnhancer: showcaseSchemaEnhancer,
    },
    {
      id: 'fullteaser',
      title: 'Destaque Completo',
      template: FullTeaserBlockTemplate,
      schemaEnhancer: fullTeaserSchemaEnhancer,
    },
  ];

  // Only add variations that don't already exist
  newVariations.forEach((newVar) => {
    const exists = teaserVariations.some(
      (existing: any) => existing.id === newVar.id,
    );
    if (!exists) {
      teaserVariations.push(newVar);
    }
  });

  (config.blocks.blocksConfig as any).teaser.variations = teaserVariations;

  return config;
}

export default function install(config: ConfigType) {
  installBlocks(config);
  defaultBlocks(config);
  installVariations(config);

  // Configure Slider Block with custom schema enhancer
  if ((config.blocks.blocksConfig as any).slider) {
    (config.blocks.blocksConfig as any).slider = {
      ...(config.blocks.blocksConfig as any).slider,
      schemaEnhancer: sliderBlockSchemaEnhancer,
    };
  }

  const localBlocks = [
    'cardBlock',
    'mainImageBlock',
    'slider',
    '__button',
    'listing',
  ];

  ['gridBlock'].forEach((blockId) => {
    const block = (config.blocks.blocksConfig as any)[blockId];
    if (
      block !== undefined &&
      block.allowedBlocks !== undefined &&
      block.blocksConfig !== undefined
    ) {
      block.allowedBlocks = [...block.allowedBlocks, ...localBlocks];
      localBlocks.forEach((localBlockId) => {
        block.blocksConfig[localBlockId] = cloneDeep(
          (config.blocks.blocksConfig as any)[localBlockId],
        );
      });
    }
  });

  // Define custom themes - this must be done AFTER all block configurations
  const customThemes = [
    {
      style: {
        '--theme-color': 'var(--theme-0)',
        '--theme-foreground-color': 'var(--default-theme-foreground-color)',
        '--theme-high-foreground-color':
          'var(--default-theme-high-foreground-color)',
        '--theme-hover-high-foreground-color':
          'var(--default-theme-hover-high-foreground-color)',
        '--theme-low-foreground-color':
          'var(--default-theme-low-foreground-color)',

        '--theme-high-contrast-color': 'var(--theme-20)',
        '--theme-hover-high-contrast-color': 'var(--theme-40)',
        '--theme-high-contrast-foreground-color':
          'var(--default-theme-foreground-color)',
        '--theme-high-high-contrast-foreground-color':
          'var(--default-theme-high-foreground-color)',
        '--theme-hover-high-high-contrast-foreground-color':
          'var(--default-theme-hover-high-foreground-color)',
        '--theme-low-contrast-foreground-color':
          'var(--default-theme-low-foreground-color)',

        '--theme-high-contrast-alt-foreground-color': 'var(--theme-60)',

        '--theme-border-color': 'var(--theme-60)',
      },
      name: 'default',
      label: 'Default',
    },
    {
      style: {
        '--theme-color': 'var(--theme-60)',
        '--theme-foreground-color': 'var(--alt-theme-foreground-color)',
        '--theme-high-foreground-color':
          'var(--alt-theme-high-foreground-color)',
        '--theme-hover-high-foreground-color':
          'var(--alt-theme-hover-high-foreground-color)',
        '--theme-low-foreground-color': 'var(--alt-theme-low-foreground-color)',

        '--theme-high-contrast-color': 'var(--theme-20)',
        '--theme-hover-high-contrast-color': 'var(--theme-40)',
        '--theme-high-contrast-foreground-color':
          'var(--default-theme-foreground-color)',
        '--theme-high-high-contrast-foreground-color':
          'var(--default-theme-high-foreground-color)',
        '--theme-hover-high-high-contrast-foreground-color':
          'var(--default-theme-hover-high-foreground-color)',
        '--theme-low-contrast-foreground-color':
          'var(--default-theme-low-foreground-color)',

        '--theme-high-contrast-alt-foreground-color': 'var(--theme-60)',

        '--theme-border-color': 'transparent',
      },
      name: 'alt',
      label: 'Alt',
    },
    {
      style: {
        '--theme-color': 'var(--theme-20)',
        '--theme-foreground-color': 'var(--default-theme-foreground-color)',
        '--theme-high-foreground-color':
          'var(--default-theme-high-foreground-color)',
        '--theme-hover-high-foreground-color':
          'var(--default-theme-hover-high-foreground-color)',
        '--theme-low-foreground-color':
          'var(--default-theme-low-foreground-color)',

        '--theme-high-contrast-color': 'var(--theme-60)',
        '--theme-hover-high-contrast-color': 'var(--theme-80)',
        '--theme-high-contrast-foreground-color':
          'var(--alt-theme-foreground-color)',
        '--theme-high-high-contrast-foreground-color':
          'var(--alt-theme-high-foreground-color)',
        '--theme-hover-high-high-contrast-foreground-color':
          'var(--alt-theme-hover-high-foreground-color)',
        '--theme-low-contrast-foreground-color':
          'var(--alt-theme-low-foreground-color)',

        '--theme-high-contrast-alt-foreground-color': 'var(--theme-0)',

        '--theme-border-color': 'transparent',
      },
      name: 'sublime',
      label: 'Sublime',
    },
    {
      style: {
        '--theme-color': 'var(--theme-100)',
        '--theme-foreground-color': 'var(--alt-theme-foreground-color)',
        '--theme-high-foreground-color':
          'var(--alt-theme-high-foreground-color)',
        '--theme-hover-high-foreground-color':
          'var(--alt-theme-hover-high-foreground-color)',
        '--theme-low-foreground-color': 'var(--alt-theme-low-foreground-color)',

        '--theme-high-contrast-color': 'var(--theme-60)',
        '--theme-hover-high-contrast-color': 'var(--theme-80)',
        '--theme-high-contrast-foreground-color':
          'var(--alt-theme-foreground-color)',
        '--theme-high-high-contrast-foreground-color':
          'var(--alt-theme-high-foreground-color)',
        '--theme-hover-high-high-contrast-foreground-color':
          'var(--alt-theme-hover-high-foreground-color)',
        '--theme-low-contrast-foreground-color':
          'var(--alt-theme-low-foreground-color)',

        '--theme-high-contrast-alt-foreground-color': 'var(--theme-0)',

        '--theme-border-color': 'transparent',
      },
      name: 'dark',
      label: 'Dark',
    },
  ];

  // Set global themes
  config.blocks.themes = customThemes;

  // Override gridBlock themes specifically to ensure they use our custom themes
  if ((config.blocks.blocksConfig as any).gridBlock) {
    (config.blocks.blocksConfig as any).gridBlock.themes = customThemes;
    (config.blocks.blocksConfig as any).gridBlock.blocksConfig = {
      ...(config.blocks.blocksConfig as any).gridBlock.blocksConfig,
      themes: customThemes,
    };
  }

  return config;
}
